import { useState } from 'react';
import { subDays, format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  AlertCircle,
  TrendingDown,
  Star,
  UserCog,
  Hourglass,
  AreaChart,
  FileText,
  Banknote,
  Users,
  Scissors,
  Award,
  ChevronDown,
  ChevronUp,
  Smile,
  Meh,
  Frown,
  Activity,
  Target,
  Wallet,
  Percent,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { DraggableBlocksContainer } from './DraggableBlocksContainer';
import { QuickActionsBar } from './QuickActionsBar';
import { AwaitingMyActionCard } from './AwaitingMyActionCard';
import {
  GovernanceAlertsBlock,
  GovernanceApprovalsBlock,
} from './Case360GovernancePanel';
import { TeamLoadTable } from './TeamLoadTable';
import { TeamActivityCard } from './TeamActivityCard';
import { RevenueChart } from './charts/RevenueChart';
import { WriteOffChart } from './charts/WriteOffChart';
import { useMDStatistics } from '../hooks/useMDStatistics';
import { useMDNps } from '../hooks/useMDNps';
import { useMDRevenueGraph } from '../hooks/useMDRevenueGraph';
import { useMDPlSummary } from '../hooks/useMDPlSummary';
import { useMDTeamEfficiency } from '../hooks/useMDTeamEfficiency';
import { TeamRangePicker, type TeamDateRange } from './TeamRangePicker';
import { useMDWorkQuality } from '../hooks/useMDWorkQuality';
import { useMDClientDebt } from '../hooks/useMDClientDebt';
import { useMDWriteOff } from '../hooks/useMDWriteOff';
import { useMDUtilization } from '../hooks/useMDUtilization';
import { useMDExpensiveRoles } from '../hooks/useMDExpensiveRoles';
import { useYearlyPlan } from '../hooks/useYearlyPlan';
import { OverdueInvoicesTable } from './OverdueInvoicesTable';
import { usePersonalBlocks } from './personalBlocks';
import type {
  StatisticPeriod,
  MDUtilizationPractice,
  MDUtilizationLawyer,
} from '../api/dashboard.api';

type Period = StatisticPeriod;

function formatUSD(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

function signedPct(value: number | null | undefined): string {
  const n = value ?? 0;
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
}

function PeriodSelector({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  const periods: { key: Period; label: string }[] = [
    { key: 'YTD', label: 'YTD' },
    { key: 'MTD', label: 'MTD' },
    { key: 'QTD', label: 'QTD' },
    { key: 'HY', label: 'HY' },
  ];
  return (
    <div className="flex w-fit items-center border border-slate-200 rounded-md shadow-xs overflow-hidden">
      {periods.map(p => (
        <button
          key={p.key}
          onClick={() => onChange(p.key)}
          className={`px-4 py-1.5 border-r border-slate-200 last:border-r-0 text-[0.8125rem] font-medium transition-colors cursor-pointer ${
            value === p.key
              ? 'bg-[#E6F4FF] text-[#0069D1] font-semibold'
              : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function StatCard({ label, value, sub, valueClass }: { label: string; value: string; sub: React.ReactNode; valueClass?: string }) {
  return (
    <div className="bg-gradient-to-t from-white to-[rgba(0,105,209,0.04)] border border-slate-200 rounded-xl shadow-xs p-6 hover:shadow-md transition-shadow">
      <div className="text-[0.75rem] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums leading-[1.3] ${valueClass || 'text-slate-900'}`}>{value}</div>
      <div className="text-[0.8125rem] mt-1 flex items-center gap-1.5">{sub}</div>
    </div>
  );
}

// Block ④ Financial KPIs — per-KPI accent tones, matching the Pending-Approvals
// tile aesthetic (colored panel border + icon chip).
const KPI_TONES = {
  revenue: { panel: 'border-sky-100', iconChip: 'bg-sky-50 text-sky-600' },
  plan: { panel: 'border-emerald-100', iconChip: 'bg-emerald-50 text-emerald-600' },
  profit: { panel: 'border-violet-100', iconChip: 'bg-violet-50 text-violet-600' },
  collection: { panel: 'border-amber-100', iconChip: 'bg-amber-50 text-amber-600' },
} as const;

// Financial-KPI tile: same shape as the Pending-Approvals tiles (icon chip →
// value → label) plus a small sub line. Not clickable — the spec's "card →
// financial report" drill-down targets the Lot 3 §7.3 report, which isn't built.
function KpiTile({
  tone,
  icon,
  label,
  value,
  sub,
  valueClass,
}: {
  tone: { panel: string; iconChip: string };
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <div className={`flex flex-col items-start rounded-lg border bg-slate-50/60 p-3 ${tone.panel}`}>
      <span className={`flex h-7 w-7 items-center justify-center rounded-md ${tone.iconChip}`}>{icon}</span>
      <span className={`mt-2 text-2xl font-bold leading-none tabular-nums ${valueClass || 'text-slate-900'}`}>{value}</span>
      <span className="mt-1 truncate text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      {sub != null && <span className="mt-0.5 text-[11px] leading-tight">{sub}</span>}
    </div>
  );
}

function AlertItem({
  severity,
  icon,
  title,
  description,
  value,
  threshold,
}: {
  severity: 'critical' | 'warning';
  icon: React.ReactNode;
  title: string;
  description: string;
  value: string;
  threshold: string;
}) {
  const isCritical = severity === 'critical';
  return (
    <div
      className={`flex items-center gap-3.5 px-4 py-3 rounded-lg border ${
        isCritical ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
      }`}
    >
      <div
        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${
          isCritical ? 'bg-red-100 text-red-700' : 'bg-amber-200 text-amber-700'
        }`}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[0.875rem] font-semibold text-slate-900 mb-0.5">{title}</div>
        <div className="text-[0.8125rem] text-slate-500">{description}</div>
      </div>
      <div className={`text-lg font-bold tabular-nums whitespace-nowrap ${isCritical ? 'text-red-700' : 'text-amber-700'}`}>
        {value}
        <span className="text-[0.75rem] font-medium text-slate-400 ml-1">{threshold}</span>
      </div>
    </div>
  );
}

function utilizationColor(pct: number) {
  if (pct >= 75) return '#16a34a';
  if (pct >= 60) return '#f59e0b';
  return '#ef4444';
}

function UtilizationRow({ name, pct, to }: { name: string; pct: number | null; to?: string }) {
  const safePct = pct ?? 0;
  const color = utilizationColor(safePct);
  const inner = (
    <>
      <span className="w-[120px] text-[0.8125rem] font-medium text-slate-900 shrink-0 truncate" title={name}>{name}</span>
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(safePct, 100)}%`, backgroundColor: color }} />
      </div>
      <span className="w-10 text-[0.8125rem] font-semibold text-right text-slate-900 tabular-nums shrink-0">{safePct.toFixed(1)}%</span>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className="flex items-center gap-3 py-1.5 -mx-2 px-2 rounded-md hover:bg-slate-50 transition-colors cursor-pointer"
      >
        {inner}
      </Link>
    );
  }
  return <div className="flex items-center gap-3 py-1.5">{inner}</div>;
}

/** One practice utilization bar; expands to its lawyers (each links to KPI). */
// Block ⑨ Quality — Top/Bottom performers **by K1** (§7.1). K1 is the weighted
// reviewer QA score on the 0–5 scale, straight from the server; it is not
// derived from client or manager feedback (that is K2/K3).
function PerformerList({
  title,
  rows,
  tone,
}: {
  title: string;
  rows: { id: number; name: string | null; k1: number | null }[];
  tone: 'top' | 'bottom';
}) {
  const { t } = useTranslation('common');
  const headingCls = tone === 'bottom' ? 'text-red-700' : 'text-slate-600';
  const scoreCls = tone === 'bottom' ? 'text-red-600' : 'text-emerald-600';
  return (
    <div>
      <div className={`mb-2 text-[0.75rem] font-semibold uppercase tracking-wider ${headingCls}`}>
        {title}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400">{t('app.noData')}</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {rows.map(r => (
            <div key={r.id} className="flex items-center justify-between gap-3">
              <Link
                to={`/app/users/${r.id}`}
                className="truncate text-sm font-medium text-slate-900 hover:text-[#0069D1] hover:underline"
              >
                {r?.name ?? `#${r.id}`}
              </Link>
              <span className={`shrink-0 text-sm font-bold tabular-nums ${scoreCls}`}>
                {r?.k1 != null ? r.k1.toFixed(1) : '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PracticeUtilRow({ practice }: { practice: MDUtilizationPractice }) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const color = utilizationColor(practice.utilization);
  const hasLawyers = practice.lawyers.length > 0;
  // The "no practice area" bucket comes back with a null id; its server-side
  // name is an English literal, so the label is resolved here instead (§5.10).
  const name =
    practice.practiceAreaId == null ? t('dashboard.unassigned') : practice.name;
  return (
    <div className="border-b border-slate-50 last:border-b-0">
      <button
        type="button"
        onClick={() => hasLawyers && setOpen(v => !v)}
        className={`flex w-full items-center gap-3 py-1.5 -mx-2 px-2 rounded-md transition-colors ${hasLawyers ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'}`}
      >
        <span className="w-[120px] shrink-0 truncate text-left text-[0.8125rem] font-medium text-slate-900" title={name}>
          {name}
        </span>
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${Math.min(practice.utilization, 100)}%`, backgroundColor: color }}
          />
        </div>
        <span className="w-10 shrink-0 text-right text-[0.8125rem] font-semibold tabular-nums text-slate-900">
          {practice.utilization.toFixed(0)}%
        </span>
        {hasLawyers ? (
          open ? <ChevronUp size={14} className="shrink-0 text-slate-400" /> : <ChevronDown size={14} className="shrink-0 text-slate-400" />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
      </button>
      {/* D-05 — every ratio states its own numerator and denominator, so the
          practice figures can be checked instead of inferred. */}
      <div className="-mt-0.5 mb-1 pl-[120px] text-[0.6875rem] text-slate-400">
        {t('dashboard.utilRowBasis', {
          billable: practice.billableHours.toFixed(0),
          capacity: practice.capacityHours.toFixed(0),
          people: practice.lawyerCount,
          share: (practice.shareOfFirmBillable ?? 0).toFixed(0),
        })}
      </div>
      {open && hasLawyers && (
        <div className="mb-2 ml-4 border-l-2 border-slate-100 pl-3">
          {practice.lawyers.map(l => (
            <UtilizationRow key={l.id} name={l.name ?? '—'} pct={l.utilization} to={`/app/kpi?userId=${l.id}`} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * D-05 — the firm-wide by-lawyer breakdown. The block was captioned "firm, by
 * practice, by lawyer" but only the first two were rendered (the per-lawyer
 * list existed solely inside a collapsed practice row). Unlike the practice
 * ratios, these share the firm's denominator model — one lawyer, one capacity —
 * so they reconcile with the firm figure.
 */
function LawyerUtilList({ lawyers }: { lawyers: MDUtilizationLawyer[] }) {
  const { t } = useTranslation('common');
  if (lawyers.length === 0) {
    return <div className="py-4 text-sm text-slate-400">{t('app.noData')}</div>;
  }
  return (
    <div className="max-h-[220px] overflow-y-auto pr-1">
      {lawyers.map((l) => (
        <div key={l.id} className="border-b border-slate-50 py-1 last:border-b-0">
          <UtilizationRow
            name={l.name ?? '—'}
            pct={l.utilization}
            to={`/app/kpi?userId=${l.id}`}
          />
          <div className="pl-1 text-[0.6875rem] text-slate-400">
            {t('dashboard.utilLawyerBasis', {
              billable: l.billableHours.toFixed(0),
              capacity: l.capacityHours.toFixed(0),
              share: (l.shareOfFirmBillable ?? 0).toFixed(0),
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Gauge({
  pct,
  label,
  targetLabel,
}: {
  pct: number | null;
  label: string;
  /** Localised target caption; the 75% figure is this system's own convention. */
  targetLabel: string;
}) {
  const safePct = pct ?? 0;
  const color = utilizationColor(safePct);
  const display = safePct.toFixed(1);
  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="relative w-40 h-40 rounded-full flex items-center justify-center shrink-0"
        style={{ background: `conic-gradient(${color} 0% ${safePct}%, #f1f5f9 ${safePct}% 100%)` }}
      >
        <div className="absolute inset-[14px] bg-white rounded-full" />
        <div className="relative flex flex-col items-center gap-0.5">
          <div className="text-[2rem] font-bold text-slate-900 tabular-nums leading-none">{display}%</div>
          <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        </div>
      </div>
      <div className="text-[0.75rem] text-slate-500 text-center">{targetLabel}</div>
    </div>
  );
}

/** NPS semicircular gauge — score scale is −100…+100. */
function NpsGauge({ nps }: { nps: number }) {
  const clamped = Math.max(-100, Math.min(100, nps));
  const pct = (clamped + 100) / 200; // 0..1 along the semicircle
  const radius = 80;
  const halfCircumference = Math.PI * radius;
  const color = clamped >= 50 ? '#16a34a' : clamped >= 0 ? '#f59e0b' : '#dc2626';

  return (
    <svg viewBox="0 0 200 118" className="w-full max-w-[250px]">
      <path
        d="M 20 100 A 80 80 0 0 1 180 100"
        fill="none"
        stroke="#f1f5f9"
        strokeWidth="14"
        strokeLinecap="round"
      />
      <path
        d="M 20 100 A 80 80 0 0 1 180 100"
        fill="none"
        stroke={color}
        strokeWidth="14"
        strokeLinecap="round"
        strokeDasharray={`${halfCircumference * pct} ${halfCircumference}`}
        className="transition-all duration-700"
      />
      <text x="100" y="84" textAnchor="middle" className="tabular-nums" fontSize="36" fontWeight="800" fill={color}>
        {clamped > 0 ? '+' : ''}
        {clamped}
      </text>
      <text x="100" y="100" textAnchor="middle" fontSize="10" fontWeight="600" letterSpacing="2" fill="#94a3b8">
        NPS
      </text>
      <text x="20" y="116" textAnchor="middle" fontSize="9" fill="#cbd5e1">−100</text>
      <text x="180" y="116" textAnchor="middle" fontSize="9" fill="#cbd5e1">+100</text>
    </svg>
  );
}

/** One NPS segment row: icon chip, label, count + share, and a thin meter. */
function NpsBreakdownRow({
  icon,
  label,
  count,
  total,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  total: number;
  tone: { chip: string; bar: string; text: string };
}) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${tone.chip}`}>{icon}</span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700">{label}</span>
        <span className="shrink-0 text-sm font-bold tabular-nums text-slate-900">{count}</span>
        <span className={`w-12 shrink-0 text-right text-xs font-semibold tabular-nums ${tone.text}`}>
          {total > 0 ? `${pct.toFixed(0)}%` : '—'}
        </span>
      </div>
      <div className="mt-1.5 ml-8 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full transition-all duration-700 ${tone.bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function AgingItem({ label, value, bg, color, to }: { label: string; value: string; bg: string; color: string; to?: string }) {
  const inner = (
    <>
      <div className="text-[0.6875rem] font-semibold uppercase tracking-[0.04em] mb-1">{label}</div>
      <div className="text-xl font-bold tabular-nums">{value}</div>
    </>
  );
  const cls = `flex-1 min-w-[120px] px-4 py-3.5 rounded-lg text-center transition-opacity ${to ? 'hover:opacity-80 cursor-pointer' : ''}`;
  if (to) {
    return <Link to={to} className={cls} style={{ backgroundColor: bg, color }}>{inner}</Link>;
  }
  return <div className={cls} style={{ backgroundColor: bg, color }}>{inner}</div>;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function debtLink(daysFrom: number | null, daysTo: number | null): string {
  const today = new Date();
  const params = new URLSearchParams({ state: 'UNPAID' });
  if (daysTo !== null) {
    const max = new Date(today);
    max.setDate(today.getDate() - daysTo);
    params.set('createdAt.max', isoDate(max));
  }
  if (daysFrom !== null) {
    const min = new Date(today);
    min.setDate(today.getDate() - daysFrom);
    params.set('createdAt.min', isoDate(min));
  }
  return `/app/bills?${params.toString()}`;
}

interface MDDashboardProps {
  period: Period;
}

const TEAM_LIST_LIMIT = 6;

export function MDDashboard({ period }: MDDashboardProps) {
  const { t } = useTranslation('common');
  const [teamExpanded, setTeamExpanded] = useState(false);
  // D-05 — Utilization is captioned "firm, by practice, by lawyer"; this picks
  // which breakdown is on screen.
  const [utilBreakdown, setUtilBreakdown] = useState<'practice' | 'lawyer'>(
    'practice',
  );
  // D-01 — the §3.1.1 base blocks, prefixed so a stored MD block order can't
  // collide with a governance block of the same name.
  const personalBlocks = usePersonalBlocks('my');
  const { data: stats, isLoading: statsLoading } = useMDStatistics(period);
  const { data: npsData, isLoading: npsLoading } = useMDNps(period);
  const { data: revenueGraph, isLoading: revenueGraphLoading } = useMDRevenueGraph(period);
  const { data: plSummary, isLoading: plSummaryLoading } = useMDPlSummary(period);
  // Team efficiency has its own date range (same picker as the KPI page),
  // independent of the global period buttons. The backend returns each lawyer's
  // KPI + the firm-average KPI for that range, sorted highest→lowest.
  const [teamRange, setTeamRange] = useState<TeamDateRange>(() => {
    const to = new Date();
    return { from: subDays(to, 29), to };
  });
  const { data: teamEfficiency, isLoading: teamLoading } = useMDTeamEfficiency(teamRange);
  const { data: workQuality, isLoading: workQualityLoading } = useMDWorkQuality(period);
  const { data: clientDebt, isLoading: clientDebtLoading } = useMDClientDebt(period);
  const { data: writeOff, isLoading: writeOffLoading } = useMDWriteOff(period);
  const { data: utilization, isLoading: utilizationLoading } = useMDUtilization(period);
  // Alert state is "right now", so it deliberately ignores the period selector.
  const { data: expensiveRoles, isLoading: expensiveRolesLoading } =
    useMDExpensiveRoles();
  const { data: yearlyPlan } = useYearlyPlan();

  const revenuePercent = stats?.revenue?.percent ?? 0;
  const planPct = stats?.plan_achievement?.amount ?? 0;
  const planStatus =
    planPct >= 90
      ? { label: t('dashboard.onTrack'), bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-100', dot: 'bg-green-600' }
      : planPct >= 70
        ? { label: t('dashboard.atRisk'), bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-100', dot: 'bg-amber-500' }
        : { label: t('dashboard.behind'), bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-100', dot: 'bg-red-600' };

  const blocks = [
    // Case 360° (Stage 12) — each card is its own draggable block.
    { id: 'quick-actions', label: t('dashboard.quickActions'), content: <QuickActionsBar showSearch /> },
    { id: 'awaiting-my-action', label: t('dashboard.case360.awaitingTitle'), content: <AwaitingMyActionCard /> },
    { id: 'governance-alerts', label: t('dashboard.case360.alertsTitle'), content: <GovernanceAlertsBlock /> },
    { id: 'governance-team-load', label: t('dashboard.teamLoadTitle'), content: <TeamLoadTable period={period} /> },
    { id: 'governance-approvals', label: t('dashboard.case360.approvalsTitle'), content: <GovernanceApprovalsBlock /> },
    { id: 'team-activity', label: t('dashboard.teamActivityTitle'), content: <TeamActivityCard /> },
    // D-01 — TS Lot 2 §3.1.3 is an OVERLAY on §3.1.1, not a substitute: an MD
    // also has their own matters, hearings and deadlines. These are the same
    // components the Lawyer dashboard mounts, and each can be hidden.
    ...personalBlocks,
    {
      id: 'kpi',
      label: t('dashboard.financialKpisTitle'),
      content: (
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2 text-base">
              <Banknote size={16} className="text-slate-400" /> {t('dashboard.financialKpisTitle')}
            </CardTitle>
            <CardDescription>{t('dashboard.financialKpisDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {/* D-07 — three different money figures live on this screen and
                  the KPI page: CASH received (here), ACCRUAL/issued bills (P&L
                  below), and one person's own billed work (KPI page). A caption
                  was not enough — the 2026-08-04 review found "Revenue" and
                  "Profit" each carrying two definitions. The LABELS are now
                  distinct, so no two indicators share a name. Calculations are
                  unchanged; bills, payments and expenses are untouched. */}
              <KpiTile
                tone={KPI_TONES.revenue}
                icon={<Banknote size={16} />}
                label={`${t('dashboard.cashCollected')} (${period})`}
                value={statsLoading || !stats ? '—' : formatUSD(stats.revenue.amount)}
                sub={
                  statsLoading || !stats ? null : (
                    <>
                      <span className={`font-semibold ${revenuePercent >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {signedPct(revenuePercent)} {t('dashboard.vsPlan')}
                      </span>
                      <span className="block text-slate-400">
                        {t('dashboard.revenueSourceCash')}
                      </span>
                    </>
                  )
                }
              />
              <KpiTile
                tone={KPI_TONES.plan}
                icon={<Target size={16} />}
                label={t('dashboard.planAchievement')}
                value={statsLoading || !stats ? '—' : `${planPct.toFixed(1)}%`}
                sub={
                  statsLoading || !stats ? null : (
                    <span className={`font-semibold ${planStatus.text}`}>{planStatus.label}</span>
                  )
                }
              />
              {/* D-07/D-08 — this is cash MINUS PAYROLL, a different quantity
                  from the P&L gross profit (billed minus expenses) below. And
                  with no payroll recorded, the margin is withheld rather than
                  reported as 100.0%. */}
              <KpiTile
                tone={KPI_TONES.profit}
                icon={<Wallet size={16} />}
                label={t('dashboard.cashAfterPayroll')}
                value={statsLoading || !stats ? '—' : formatUSD(stats.profit.amount)}
                valueClass={!statsLoading && stats?.profit.amount < 0 ? 'text-red-600' : undefined}
                sub={
                  statsLoading || !stats ? null : stats?.profit?.margin == null ? (
                    <span className="font-semibold text-amber-600">
                      {t('dashboard.marginNoCost')}
                    </span>
                  ) : (
                    <span className={`font-semibold ${stats.profit.margin >= 0 ? 'text-slate-500' : 'text-red-500'}`}>
                      {t('dashboard.margin')} {stats.profit.margin.toFixed(1)}%
                      <span className="block text-slate-400">
                        {t('dashboard.afterPayrollNote', {
                          cost: formatUSD(stats?.profit?.cost ?? 0),
                        })}
                      </span>
                    </span>
                  )
                }
              />
              <KpiTile
                tone={KPI_TONES.collection}
                icon={<Percent size={16} />}
                label={t('dashboard.collectionRate')}
                value={
                  statsLoading || !stats
                    ? '—'
                    : `${(stats?.collection?.percent ?? 0).toFixed(0)}%`
                }
                valueClass={
                  !statsLoading && (stats?.collection?.percent ?? 0) < 80
                    ? 'text-red-600'
                    : 'text-green-600'
                }
                sub={
                  statsLoading || !stats ? null : (
                    // D-09 — the client tried to derive 86% from the two revenue
                    // figures on screen and got 102.1%. It isn't derivable from
                    // them: both legs come from the same bill set. Show them.
                    <span className="font-semibold text-slate-500">
                      {t('dashboard.collectedOfBilled', {
                        collected: formatUSD(stats?.collection?.collected ?? 0),
                        billed: formatUSD(stats?.collection?.billed ?? 0),
                        count: stats?.collection?.billCount ?? 0,
                      })}
                    </span>
                  )
                }
              />
            </div>
          </CardContent>
        </Card>
      ),
    },
    {
      id: 'nps-utilization',
      label: t('dashboard.npsTitle'),
      content: (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Left — Net Promoter Score */}
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Star size={16} className="text-slate-400" /> {t('dashboard.npsTitle')}
                </CardTitle>
                <CardDescription>{t('dashboard.npsDesc')}</CardDescription>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
                <span className="font-bold tabular-nums text-slate-900">{npsData?.total ?? 0}</span>
                {t('dashboard.npsResponses')}
              </span>
            </div>
          </CardHeader>
          <CardContent className="pt-5">
            {npsLoading ? (
              <div className="flex items-center justify-center h-[160px] text-slate-400 text-sm">Loading…</div>
            ) : !npsData ? null : npsData.nps === null || npsData.total === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <Star size={28} className="text-slate-200" />
                <p className="text-sm text-slate-400">{t('dashboard.npsNoData')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 items-center gap-6 sm:grid-cols-2">
                {/* Gauge */}
                <div className="flex justify-center">
                  <NpsGauge nps={npsData.nps} />
                </div>

                {/* Breakdown */}
                <div className="flex flex-col gap-4">
                  <NpsBreakdownRow
                    icon={<Smile size={14} />}
                    label={t('dashboard.npsPromoters')}
                    count={npsData.promoters}
                    total={npsData.total}
                    tone={{ chip: 'bg-green-50 text-green-600', bar: 'bg-green-500', text: 'text-green-600' }}
                  />
                  <NpsBreakdownRow
                    icon={<Meh size={14} />}
                    label={t('dashboard.npsPassives')}
                    count={npsData.passives}
                    total={npsData.total}
                    tone={{ chip: 'bg-slate-100 text-slate-500', bar: 'bg-slate-300', text: 'text-slate-500' }}
                  />
                  <NpsBreakdownRow
                    icon={<Frown size={14} />}
                    label={t('dashboard.npsDetractors')}
                    count={npsData.detractors}
                    total={npsData.total}
                    tone={{ chip: 'bg-red-50 text-red-600', bar: 'bg-red-500', text: 'text-red-600' }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right — Utilization (firm / practice / lawyer) */}
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity size={16} className="text-slate-400" /> {t('dashboard.utilizationTitle')}
            </CardTitle>
            <CardDescription>{t('dashboard.utilizationDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {utilizationLoading ? (
              <div className="flex items-center justify-center h-[160px] text-slate-400 text-sm">Loading…</div>
            ) : !utilization ? null : (
              <div className="grid items-start gap-6 md:grid-cols-1" style={{ gridTemplateColumns: '200px 1fr' }}>
                {/* Firm utilization */}
                <div className="flex flex-col items-center justify-center gap-1 py-2">
                  <div
                    className="text-[2.5rem] font-bold leading-none tabular-nums"
                    style={{ color: utilizationColor(utilization.firm.utilization) }}
                  >
                    {utilization.firm.utilization.toFixed(0)}%
                  </div>
                  <div className="text-[0.75rem] font-semibold uppercase tracking-wider text-slate-500">
                    {t('dashboard.utilFirm')}
                  </div>
                  <div className="text-[0.6875rem] text-slate-400">
                    {t('dashboard.utilBillableOfCapacity', {
                      billable: utilization.firm.billableHours.toFixed(0),
                      capacity: utilization.firm.capacityHours.toFixed(0),
                    })}
                  </div>
                </div>
                {/* D-05 — by practice AND by lawyer, as the block is captioned.
                    The two are separate views because they use different
                    denominators (see the note below). */}
                <div>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-[0.875rem] font-semibold text-slate-900">
                      <Activity size={14} className="text-[#0069D1]" />
                      {utilBreakdown === 'practice'
                        ? t('dashboard.utilByPractice')
                        : t('dashboard.utilByLawyer')}
                    </div>
                    <div className="flex gap-1 rounded-lg bg-slate-100 p-0.5">
                      {(['practice', 'lawyer'] as const).map(mode => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => setUtilBreakdown(mode)}
                          className={`rounded-md px-2.5 py-1 text-[0.75rem] font-medium transition ${
                            utilBreakdown === mode
                              ? 'bg-white text-slate-900 shadow-sm'
                              : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          {mode === 'practice'
                            ? t('dashboard.utilTabPractice')
                            : t('dashboard.utilTabLawyer')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {utilBreakdown === 'practice' ? (
                    utilization.byPractice.length === 0 ? (
                      <div className="py-4 text-sm text-slate-400">{t('app.noData')}</div>
                    ) : (
                      utilization.byPractice.map(p => (
                        <PracticeUtilRow key={p.practiceAreaId ?? p.name} practice={p} />
                      ))
                    )
                  ) : (
                    <LawyerUtilList lawyers={utilization.byLawyer ?? []} />
                  )}

                  {/* D-05 — the practice shares summed to 30% against a firm
                      26% and the client could not reconcile them. They never
                      could: the denominators differ by design. Say so. */}
                  <p className="mt-2.5 border-t border-slate-100 pt-2 text-[0.6875rem] leading-relaxed text-slate-400">
                    {t('dashboard.utilDenominatorNote', {
                      capacity: utilization.denominators?.capacityPerLawyer ?? 0,
                      days: utilization.denominators?.workingDays ?? 0,
                      hours: utilization.denominators?.dailyHours ?? 0,
                      people: utilization.firm?.lawyerCount ?? 0,
                    })}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      ),
    },
    /* {
      id: 'alerts',
      content: (
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle size={16} className="text-slate-400" /> {t('dashboard.systemAlerts')}
            </CardTitle>
            <CardDescription>{t('dashboard.systemAlertsDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="flex flex-col gap-2.5">
              <AlertItem severity="critical" icon={<AlertCircle size={14} />} title={t('dashboard.escRateAbove')} description={t('dashboard.escRateDesc')} value="6.8%" threshold="/ 5%" />
              <AlertItem severity="critical" icon={<TrendingDown size={14} />} title={t('dashboard.realizationBelow')} description={t('dashboard.realizationDesc')} value="0.76" threshold="/ 0.80" />
              <AlertItem severity="warning" icon={<UserCog size={14} />} title={t('dashboard.expensiveRoles')} description={t('dashboard.expensiveRolesDesc')} value="48h" threshold="/ 20h" />
              <AlertItem severity="warning" icon={<Hourglass size={14} />} title={t('dashboard.billingAging')} description={t('dashboard.billingAgingDesc')} value="12" threshold="/ 5" />
            </div>
          </CardContent>
        </Card>
      ),
    }, */
    {
      id: 'finance',
      label: t('dashboard.revenueVsPlan'),
      content: (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr]">
          {/* Left — Revenue vs Plan */}
          <Card>
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="flex items-center gap-2 text-base">
                <AreaChart size={16} className="text-slate-400" /> {t('dashboard.revenueVsPlan')}
              </CardTitle>
              <CardDescription>{t('dashboard.revenueVsPlanDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <RevenueChart
                data={revenueGraph}
                isLoading={revenueGraphLoading}
                monthlyPlan={stats ? stats.plan / 12 : undefined}
                yearlyPlanAmount={yearlyPlan?.amount}
              />
            </CardContent>
          </Card>

          {/* Right — P&L (top) + Cash Flow numbers (bottom), filtered by the page period */}
          <div className="flex flex-col gap-4">
            <Card>
              <CardHeader className="pb-3 border-b border-slate-100">
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText size={16} className="text-slate-400" /> {t('dashboard.plSummary')}
                </CardTitle>
                {/* Accrual, not cash — see the note on the Revenue tile above. */}
                <CardDescription>{t('dashboard.revenueSourceBilled')}</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                {plSummaryLoading ? (
                  <div className="flex items-center justify-center h-[120px] text-slate-400 text-sm">Loading…</div>
                ) : (
                  <>
                    {/* D-07 — labelled apart from the cash tiles above: this is
                        the accrual view (issued bills, recorded expenses). */}
                    {[
                      { label: t('dashboard.plRevenueBilled'), value: plSummary ? formatUSD(plSummary.total_revenue) : '—', cls: '' },
                      { label: t('dashboard.plCostExpenses'), value: plSummary ? formatUSD(plSummary.cost) : '—', cls: '' },
                      { label: t('dashboard.plGrossProfit'), value: plSummary ? formatUSD(plSummary.gross_profit) : '—', cls: `font-bold text-[0.9375rem] ${plSummary && plSummary.gross_profit < 0 ? 'text-red-600' : ''}`, total: true },
                    ].map((row, i, arr) => (
                      <div
                        key={row.label}
                        className={`flex justify-between items-center py-2.5 ${i < arr.length - 1 ? 'border-b border-slate-100' : ''} ${row.total ? 'border-t-2 border-slate-200 mt-0 pt-3' : ''}`}
                      >
                        <span className={`text-[0.875rem] font-medium text-slate-500 ${row.total ? 'font-bold text-slate-900' : ''}`}>{row.label}</span>
                        <span className={`text-[0.875rem] font-semibold tabular-nums text-slate-900 ${row.cls}`}>{row.value}</span>
                      </div>
                    ))}
                    {/* D-08 — "a margin of 100.0% must not be displayed as a
                        result". State whether a cost exists instead. */}
                    <p className="mt-2 text-[0.6875rem] leading-relaxed text-slate-400">
                      {plSummary?.margin == null
                        ? t('dashboard.plNoCostRecorded')
                        : t('dashboard.plMarginNote', {
                            margin: plSummary.margin.toFixed(1),
                            count: plSummary?.expense_count ?? 0,
                          })}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3 border-b border-slate-100">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Banknote size={16} className="text-slate-400" /> {t('dashboard.cashFlow')}
                </CardTitle>
                <CardDescription>{t('dashboard.cashFlowDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                {plSummaryLoading ? (
                  <div className="flex items-center justify-center h-[120px] text-slate-400 text-sm">Loading…</div>
                ) : (
                  (() => {
                    const cashIn = plSummary?.cash_in ?? 0;
                    const cashOut = plSummary?.cash_out ?? 0;
                    const cashBalance = plSummary?.cash_balance ?? cashIn - cashOut;
                    return (
                      <>
                        {[
                          { label: t('dashboard.cashIn'), value: plSummary ? formatUSD(cashIn) : '—', cls: 'text-slate-900' },
                          { label: t('dashboard.cashOut'), value: plSummary ? formatUSD(cashOut) : '—', cls: 'text-slate-900' },
                          { label: t('dashboard.cashBalance'), value: plSummary ? formatUSD(cashBalance) : '—', cls: `font-bold text-[0.9375rem] ${cashBalance < 0 ? 'text-red-600' : 'text-slate-900'}`, total: true },
                        ].map((row, i, arr) => (
                          <div
                            key={row.label}
                            className={`flex justify-between items-center py-2.5 ${i < arr.length - 1 ? 'border-b border-slate-100' : ''} ${row.total ? 'border-t-2 border-slate-200 mt-0 pt-3' : ''}`}
                          >
                            <span className={`text-[0.875rem] font-medium text-slate-500 ${row.total ? 'font-bold text-slate-900' : ''}`}>{row.label}</span>
                            <span className={`text-[0.875rem] font-semibold tabular-nums ${row.cls}`}>{row.value}</span>
                          </div>
                        ))}
                        {/* D-08 — Cash Out of $0 means "no accepted expenses in
                            this period", which is a different statement from
                            "the Expenses module is not connected". */}
                        {cashOut === 0 && (
                          <p className="mt-2 text-[0.6875rem] leading-relaxed text-slate-400">
                            {t('dashboard.cashOutNoExpenses')}
                          </p>
                        )}
                      </>
                    );
                  })()
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ),
    },
    {
      id: 'team',
      label: t('dashboard.teamEfficiency'),
      content: (
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users size={16} className="text-slate-400" /> {t('dashboard.teamEfficiency')}
                </CardTitle>
                <CardDescription>{t('dashboard.teamEfficiencyDesc')}</CardDescription>
              </div>
              <TeamRangePicker value={teamRange} onChange={setTeamRange} />
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {teamLoading ? (
              <div className="flex items-center justify-center h-[200px] text-slate-400 text-sm">Loading…</div>
            ) : (
              (() => {
                const sortedLawyer = teamEfficiency?.by_lawyer ?? [];
                const hasMore = sortedLawyer.length > TEAM_LIST_LIMIT;
                const remaining = Math.max(sortedLawyer.length - TEAM_LIST_LIMIT, 0);
                const visibleLawyer = teamExpanded ? sortedLawyer : sortedLawyer.slice(0, TEAM_LIST_LIMIT);
                // Clicking a lawyer opens the KPI page pre-filled with that user
                // and the same date range as the team-efficiency picker.
                const kpiLink = (id: number) => {
                  const params = new URLSearchParams({ userId: String(id) });
                  if (teamRange.from) params.set('from', format(teamRange.from, 'yyyy-MM-dd'));
                  if (teamRange.to) params.set('to', format(teamRange.to, 'yyyy-MM-dd'));
                  return `/app/kpi?${params.toString()}`;
                };
                return (
                  <div className="col-span-full">
                    <div className="grid gap-6 items-start" style={{ gridTemplateColumns: '200px 1fr' }}>
                      <Gauge
                        pct={teamEfficiency?.firm_kpi ?? 0}
                        label={t('dashboard.firmAvg')}
                        targetLabel={t('dashboard.targetPct', { value: 75 })}
                      />
                      <div>
                        <div className="flex items-center gap-2 text-[0.875rem] font-semibold text-slate-900 mb-2">
                          <Users size={14} className="text-[#0069D1]" /> {t('dashboard.byLawyer')}
                        </div>
                        {visibleLawyer.map(r => (
                          <UtilizationRow key={r.id} name={r.name ?? '—'} pct={r.kpi} to={kpiLink(r.id)} />
                        ))}
                      </div>
                    </div>
                    {hasMore && (
                      <div className="mt-3 flex justify-center">
                        <button
                          onClick={() => setTeamExpanded(v => !v)}
                          className="flex items-center gap-1 text-[0.8125rem] font-medium text-[#0069D1] hover:text-[#0054a8] transition-colors"
                        >
                          {teamExpanded ? (
                            <><ChevronUp size={14} />{t('dashboard.showLess')}</>
                          ) : (
                            <><ChevronDown size={14} />{t('dashboard.showMore', { count: remaining })}</>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </CardContent>
        </Card>
      ),
    },
    {
      id: 'writeoff',
      label: t('dashboard.writeOffTitle'),
      content: (
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2 text-base">
              <Scissors size={16} className="text-slate-400" /> {t('dashboard.writeOffTitle')}
            </CardTitle>
            <CardDescription>{t('dashboard.writeOffDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {writeOffLoading ? (
              <div className="flex items-center justify-center h-[180px] text-slate-400 text-sm">Loading…</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {/* Each rate carries its own denominator (display rule D1), and
                      the write-off base is now the same task cohort Utilization
                      measures, so the two blocks reconcile (§6.6). */}
                  <StatCard
                    label={t('dashboard.writeOffRate')}
                    value={`${writeOff?.write_off?.percent ?? 0}%`}
                    sub={
                      <span className="font-semibold text-red-600">
                        {t('dashboard.writeOffOfWorked', {
                          lost: ((writeOff?.write_off?.lost_seconds ?? 0) / 3600).toFixed(1),
                          worked: ((writeOff?.write_off?.worked_seconds ?? 0) / 3600).toFixed(1),
                        })}
                      </span>
                    }
                  />
                  <StatCard
                    label={t('dashboard.manualAdjustments')}
                    value={String(writeOff?.adjustments?.count ?? 0)}
                    sub={
                      <span className="font-semibold text-amber-700">
                        {t('dashboard.adjOfWorked', {
                          percent: writeOff?.adjustments?.percent ?? 0,
                          base: ((writeOff?.adjustments?.base_seconds ?? 0) / 3600).toFixed(1),
                        })}
                      </span>
                    }
                  />
                  {/* D-10 — this figure and the Alerts strip's both used to read
                      "ESC Rate". Same formula, different window: the label now
                      carries the period so the two can't be read as one number
                      disagreeing with itself. */}
                  <StatCard
                    label={t('dashboard.escalationRatePeriod', { period })}
                    value={`${writeOff?.escalations?.percent ?? 0}%`}
                    sub={
                      <span className="font-semibold text-red-600">
                        {t('dashboard.escalatedOfTasks', {
                          escalated: writeOff?.escalations?.escalated ?? 0,
                          total: writeOff?.escalations?.total_tasks ?? 0,
                        })}
                      </span>
                    }
                  />
                </div>
                <WriteOffChart data={writeOff?.trend ?? []} />
              </>
            )}
          </CardContent>
        </Card>
      ),
    },
    {
      id: 'quality',
      label: t('dashboard.workQualityTitle'),
      content: (() => {
        // Average K1 (§7.1) — the weighted reviewer QA score, server-computed.
        // Rows arrive sorted best-K1 first, so Top is the head and Bottom the tail.
        const rated = (workQuality?.by_lawyer ?? []).filter(l => l?.k1 != null);
        const topCount = Math.min(3, rated.length);
        const top = rated.slice(0, topCount);
        const bottomCount = Math.min(3, rated.length - topCount);
        const bottom = bottomCount > 0 ? rated.slice(rated.length - bottomCount).reverse() : [];
        const firmAvg = workQuality?.avg_k1 ?? null;
        return (
          <Card>
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="flex items-center gap-2 text-base">
                <Award size={16} className="text-slate-400" /> {t('dashboard.workQuality')}
              </CardTitle>
              <CardDescription>{t('dashboard.qualityK1Desc')}</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {workQualityLoading ? (
                <div className="flex items-center justify-center h-[140px] text-slate-400 text-sm">Loading…</div>
              ) : (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-[auto_1fr_1fr] md:gap-8">
                  <div className="md:border-r md:border-slate-100 md:pr-8">
                    <div className="text-[0.75rem] font-medium uppercase tracking-wider text-slate-500">{t('dashboard.firmAvgK1')}</div>
                    <div className="mt-1 text-[2.5rem] font-bold leading-none text-slate-900 tabular-nums">
                      {firmAvg != null ? firmAvg.toFixed(1) : '—'}
                    </div>
                    {/* The numbers behind the average, so it can be checked (D1). */}
                    <div className="mt-1.5 text-[0.8125rem] text-slate-400">
                      {t('dashboard.k1Scale', { scale: workQuality?.k1_scale ?? 5 })}
                    </div>
                    <div className="mt-0.5 text-[0.75rem] text-slate-400">
                      {t('dashboard.k1RatedLawyers', {
                        count: workQuality?.k1_rated_lawyers ?? 0,
                      })}
                    </div>
                    {/* K3 is a different component of the §10.1 formula — shown,
                        but never presented as K1. */}
                    <div className="mt-2 border-t border-slate-100 pt-2 text-[0.75rem] text-slate-500">
                      {t('dashboard.avgK3', {
                        value: workQuality?.avg_k3 != null ? workQuality.avg_k3.toFixed(1) : '—',
                        scale: workQuality?.k3_scale ?? 5,
                        count: workQuality?.k3_rated_lawyers ?? 0,
                      })}
                    </div>
                  </div>
                  <PerformerList title={t('dashboard.topPerformers')} rows={top} tone="top" />
                  <PerformerList title={t('dashboard.bottomPerformers')} rows={bottom} tone="bottom" />
                </div>
              )}
            </CardContent>
          </Card>
        );
      })(),
    },
    {
      // TZ Lot 1 §7.1 / §8.2 — the expensive-role alert. Present as a daily
      // notification cron since the billing review, but never on screen until
      // now (2026-07-26 review, Not-implemented #1).
      id: 'expensive-roles',
      label: t('dashboard.expensiveRoles'),
      content: (
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2 text-base">
              <UserCog size={16} className="text-slate-400" />{' '}
              {t('dashboard.expensiveRolesTitle')}
            </CardTitle>
            <CardDescription>
              {t('dashboard.expensiveRolesDesc', {
                share: Math.round((expensiveRoles?.thresholds?.share ?? 0.3) * 100),
                rate: expensiveRoles?.thresholds?.ratePerHour ?? 200,
                minHours: expensiveRoles?.thresholds?.minBillableHours ?? 10,
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {expensiveRolesLoading ? (
              <div className="flex h-[80px] items-center justify-center text-sm text-slate-400">
                {t('app.loading')}
              </div>
            ) : !expensiveRoles?.count ? (
              <p className="py-4 text-sm text-slate-400">
                {t('dashboard.expensiveRolesNone')}
              </p>
            ) : (
              <div className="flex flex-col gap-2.5">
                {expensiveRoles.matters.map(m => (
                  <AlertItem
                    key={m.id}
                    severity={m.share > 50 ? 'critical' : 'warning'}
                    icon={<UserCog size={14} />}
                    title={m?.name ?? `#${m.id}`}
                    // The hours behind the share, so the % is checkable (D1).
                    description={t('dashboard.expensiveRolesRow', {
                      expensive: m.expensiveHours.toFixed(1),
                      total: m.billableHours.toFixed(1),
                    })}
                    value={`${m.share.toFixed(1)}%`}
                    threshold={`/ ${Math.round((expensiveRoles?.thresholds?.share ?? 0.3) * 100)}%`}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ),
    },
    {
      id: 'debt',
      label: t('dashboard.clientDebt'),
      content: (
        <Card>
          <CardHeader className="flex-row items-center justify-between pb-3 border-b border-slate-100">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText size={16} className="text-slate-400" /> {t('dashboard.clientDebt')}
              </CardTitle>
              <CardDescription>{t('dashboard.clientDebtDesc')}</CardDescription>
            </div>
            <div className="text-right">
                <div className="text-[0.75rem] font-medium uppercase tracking-wider text-slate-500">{t('dashboard.totalOutstanding')}</div>
                <div className="text-xl font-bold text-slate-900 tabular-nums">
                  {clientDebtLoading || !clientDebt ? '—' : formatUSD(
                    clientDebt.last_30_day + clientDebt.from_30_to_60 + clientDebt.from_60_to_90 + clientDebt.more_than_90
                  )}
                </div>
              </div>
          </CardHeader>
          <CardContent className="pt-4">
            {clientDebtLoading ? (
              <div className="flex items-center justify-center h-[80px] text-slate-400 text-sm">Loading…</div>
            ) : (
              <div className="flex gap-3 flex-wrap">
                <AgingItem label={t('dashboard.aging0to30')} value={clientDebt ? formatUSD(clientDebt.last_30_day) : '—'} bg="#f0fdf4" color="#15803d" to={debtLink(30, 0)} />
                <AgingItem label={t('dashboard.aging30to60')} value={clientDebt ? formatUSD(clientDebt.from_30_to_60) : '—'} bg="#fffbeb" color="#b45309" to={debtLink(60, 30)} />
                <AgingItem label={t('dashboard.aging60to90')} value={clientDebt ? formatUSD(clientDebt.from_60_to_90) : '—'} bg="#fff7ed" color="#ea580c" to={debtLink(90, 60)} />
                <AgingItem label={t('dashboard.aging90plus')} value={clientDebt ? formatUSD(clientDebt.more_than_90) : '—'} bg="#fef2f2" color="#b91c1c" to={debtLink(null, 90)} />
              </div>
            )}

            {/* D-04 — TS §7.1 requires "Collection Rate, AR aging, overdue
                invoices". Only the aging summary was here; this is the list. */}
            <OverdueInvoicesTable />
          </CardContent>
        </Card>
      ),
    },
  ];

  return <DraggableBlocksContainer storageKey="andersen.md.blockOrder" blocks={blocks} />;
}
